import { gql } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import cookie from 'cookie';
import {
  UseUserQuery,
  UseUserQuery_user as UseUserQueryUser
} from '../../generated/types/UseUserQuery';
import { ONE_YEAR_SECONDS } from '../../constants';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { initializeCache } from '../../lib/apollo/cache';
import { parseUser, NULL_USER, serializeUser } from '../../lib/user';
import { logoutOnAmazon } from '../../lib/amazon';
import { reportError } from '../../lib/reportError';
import { useLocalQuery } from '../useLocalQuery';
import { useCookies, getCookies } from '../useCookies';
import { tc } from '../../lib/trackingContext';

export { UPDATE_USER_MUTATION } from './useUpdateUserMutation/useUpdateUserMutation.rest';

export const typeDefs = gql`
  type User {
    accessToken: String
    email: String
    isMember: Boolean
    firstName: String
    id: String
    isLoggedIn: Boolean!
    lastName: String
    countryCode: String
    isAmazonConnected: Boolean
    isFacebookConnected: Boolean
    phone: String
    userLoyaltyStatus: Int
    statusLevel: Int
    clientTier: Int
  }
`;

export const USER_QUERY = gql`
  query UseUserQuery {
    user @client {
      accessToken
      email
      isMember
      firstName
      id
      isLoggedIn
      lastName
      countryCode
      isFacebookConnected
      isAmazonConnected
      userLoyaltyStatus
      phone
      statusLevel
      clientTier
    }
  }
`;

export interface SetUserProps extends Omit<UseUserQueryUser, '__typename'> {
  remember?: boolean;
  cartId?: number | null;
}

export const FULL_USER_COOKIE = 'fullUser';

// TODO: this is a temporary method to backfill the `isLoggedIn` flag for users with old cookies
// this should be removed after a month or so (around November 2020)
const backfillLoginFlagOnOldCookies = () => {
  const { fullUser } = getCookies();

  if (!fullUser) return;

  try {
    const user = JSON.parse(fullUser);
    if (user.isLoggedIn == null) {
      document.cookie = cookie.serialize(
        FULL_USER_COOKIE,
        serializeUser({ ...user, isLoggedIn: user.firstName != null }),
        { maxAge: ONE_YEAR_SECONDS, path: '/' }
      );
    }
  } catch (error) {
    reportError(error);
  }
};

export const getUserFromCookies = () => {
  backfillLoginFlagOnOldCookies();
  const { fullUser } = getCookies();
  return fullUser ? parseUser(fullUser) : NULL_USER;
};

export const useUser = () => {
  const { data, client, error } = useLocalQuery<UseUserQuery>(USER_QUERY);
  const { setCookie, deleteCookie } = useCookies();

  const writeUserData = useCallback(
    async (user: UseUserQueryUser) => {
      const shouldResetCache = user.isLoggedIn !== data?.user?.isLoggedIn;

      if (shouldResetCache) {
        await client.clearStore();
        initializeCache(client, { user });
        window.dispatchEvent(new CustomEvent('resetcache'));
      } else {
        client.writeQuery<UseUserQuery>({ query: USER_QUERY, data: { user } });
      }
    },
    [client, data]
  );

  const removeUserData = useCallback(() => {
    deleteCookie([FULL_USER_COOKIE, 'cartId']);
    logoutOnAmazon();

    // reloading the page doesn't work in jest, so reset the Apollo cache instead
    if (typeof jest !== 'undefined') {
      return writeUserData(NULL_USER);
    }

    // in real enviroments reload the page when logging out
    // Apollo has some issues where it tries to fetch stale queries even after resetting the cache
    // see ECOM-371
    window.location.href = '/';
  }, [writeUserData, deleteCookie]);

  const setUserData = useCallback(
    ({
      accessToken,
      email,
      isMember,
      firstName,
      id,
      isLoggedIn,
      lastName,
      countryCode,
      remember = true,
      cartId,
      isAmazonConnected,
      isFacebookConnected,
      userLoyaltyStatus,
      phone,
      statusLevel,
      clientTier
    }: SetUserProps) => {
      const fullUser = serializeUser({
        accessToken,
        email,
        isMember,
        firstName,
        id,
        isLoggedIn,
        lastName,
        countryCode,
        isAmazonConnected,
        isFacebookConnected,
        userLoyaltyStatus,
        phone,
        statusLevel,
        clientTier
      });
      const maxAge = remember ? ONE_YEAR_SECONDS : undefined;

      setCookie(FULL_USER_COOKIE, fullUser, maxAge);
      cartId && setCookie('cartId', String(cartId), maxAge);

      return writeUserData({
        __typename: 'User',
        accessToken,
        email,
        isMember,
        firstName,
        id,
        isLoggedIn,
        lastName,
        countryCode,
        isAmazonConnected,
        isFacebookConnected,
        userLoyaltyStatus,
        phone,
        statusLevel,
        clientTier
      });
    },
    [writeUserData, setCookie]
  );

  const user = data?.user || NULL_USER;

  useEffect(() => {
    tc.setCurrent('user', user);
  }, [user]);

  /* istanbul ignore if */
  if (error) throwQueryError(error);

  return { removeUserData, setUserData, user };
};
